import { AppRoutePath, AppRedirectPath, ReporterRoutePath } from 'config/constants';

import { Suspense, lazy } from 'react';

import RequestPasswordReset from '../../UserMgmt/RequestPasswordReset';
import PasswordReset from '../../UserMgmt/CompletePasswordReset';
import FourOFour from '../../404';
import {
  Route,
  Navigate,
  useLocation,
  Outlet,
  Routes,
  useParams,
  generatePath,
} from 'react-router-dom';
import type { Location } from 'react-router-dom';
import BackgroundRoute from './BackgroundRoute';
import RouteElement from './RouteElement';
import { getCaseStudyParamsSuffix } from 'hooks/studyParams';
import ReporterBlankRoute from 'domains/reporter/ReporterBlankRoute';
import { DEFAULT_CLUSTER_ADMIN_URLS } from 'config/index';

const LoginComponent = lazy(() => import('../../UserMgmt/LoginComponent'));

const MFALoginComponent = lazy(() => import('../../UserMgmt/MFALoginComponent'));

const Signup = lazy(() => import('../../UserMgmt/Signup'));

const Logout = lazy(() => import('../../UserMgmt/Logout'));
const Worklist = lazy(() => import('../../../domains/worklist/Worklist'));
const Viewer = lazy(() => import('../../../domains/viewer/Viewer'));
const OpenWorker = lazy(() => import('domains/viewer/Viewer/OpenPixelDataSharedWorker'));
const Debug = lazy(() => import('../../../domains/viewer/Debug'));
const Ward = lazy(() => import('../../../domains/viewer/Viewer/Ward/index'));
const SystemCheck = lazy(() => import('../../SystemCheck/SystemCheckRoute'));

const Reporter = lazy(() => import('../../../domains/reporter/Reporter'));
const TemplateEditor = lazy(() => import('../../../domains/reporter/Template/Template.Editor'));
const MacroEditor = lazy(() => import('../../../domains/reporter/Macro/Editor'));

const AISandbox = lazy(() => import('../../../domains/ai-sandbox/AISandbox'));

const ReadCase = lazy(() => import('../../External/ReadCase'));
const ViewCase = lazy(() => import('../../External/ViewCase/index'));
const TemplatesBrowser = lazy(() => import('../../../domains/reporter/Browser/TemplatesBrowser'));
const MacrosBrowser = lazy(() => import('../../../domains/reporter/Browser/MacrosBrowser'));
const SearchPage = lazy(() =>
  import('../../../domains/Search').then((module) => ({
    default: module.SearchPage,
  }))
);

const PatientJacketWrapper = lazy(() =>
  import('../../../domains/PatientJacket').then((module) => ({
    default: module.PatientJacketWrapper,
  }))
);

const BlankPatientJacket = lazy(
  () => import('../../../domains/worklist/PatientJacket/PatientJacketBlankRoute')
);

const BlankViewer = lazy(() => import('../../../domains/viewer/ViewerBlankRoute'));

const BlankAISandbox = lazy(
  () => import('../../../domains/ai-sandbox/AISandbox/AISandboxBlankRoute')
);

const ResetCachePage = lazy(() => import('common/ResetCachePage'));

const Admin = () => {
  window.location.href = DEFAULT_CLUSTER_ADMIN_URLS.adminUrl;
  return null;
};

export function AppRoutes(): React.ReactElement {
  const routes = (
    <Route path="" element={<BackgroundRoute />}>
      <Route
        path={AppRoutePath.LOGIN}
        element={
          <RouteElement title="Login">
            <LoginComponent />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.VERIFY_MFA}
        element={
          <RouteElement title="Login">
            <MFALoginComponent />
          </RouteElement>
        }
      />
      {/**
       * Apollo can't set the useAuth Recoil state needed to logout
       * the user from the UI, we use this route to do it.
       */}
      <Route
        path={AppRoutePath.LOGOUT}
        element={
          <RouteElement title="Logout">
            <Logout />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.CONFIRM_ACCOUNT}
        element={
          <RouteElement title="Confirm Account">
            <Signup />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.RESET_PASSWORD}
        element={
          <RouteElement title="Password Reset">
            <RequestPasswordReset />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.COMPLETE_RESET_PASSWORD}
        element={
          <RouteElement title="Password Reset">
            <PasswordReset />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.RESET}
        element={
          <RouteElement title="Reset Cache">
            <ResetCachePage />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.WORKLIST}
        element={
          <RouteElement title="Worklist">
            <Worklist />
          </RouteElement>
        }
      >
        <Route
          path={AppRoutePath.PATIENT_JACKET}
          element={
            <RouteElement>
              <PatientJacketWrapper />
            </RouteElement>
          }
        />
      </Route>
      <Route
        path={AppRoutePath.WORKLIST_SEARCH}
        element={
          <RouteElement title="Search">
            <SearchPage />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.PATIENT_JACKET_UNDOCKED}
        element={
          <RouteElement title="Patient Jacket">
            <PatientJacketWrapper />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.PATIENT_JACKET_BLANK}
        element={
          <RouteElement title="Patient Jacket">
            <BlankPatientJacket />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.VIEWER}
        element={
          <RouteElement title="Viewer">
            <Viewer />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.VIEWER_BLANK}
        element={
          <RouteElement title="Viewer">
            <BlankViewer />
          </RouteElement>
        }
      />
      <Route path={AppRoutePath.ADMIN} element={<Admin />} />
      <Route
        path={AppRoutePath.REPORTER}
        element={
          <RouteElement title="Reporter">
            <ReporterElement />
          </RouteElement>
        }
      >
        <Route index element={<Reporter />} />
        {reporterRoutes}
      </Route>
      <Route
        path={AppRoutePath.REPORTER_BLANK}
        element={
          <RouteElement title="Reporter">
            <ReporterElement />
          </RouteElement>
        }
      >
        <Route index element={<ReporterBlankRoute />} />
        {reporterRoutes}
      </Route>
      <Route
        path={AppRoutePath.AI_SANDBOX}
        element={
          <RouteElement title="AI Sandbox">
            <AISandbox />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.AI_SANDBOX_BLANK}
        element={
          <RouteElement title="AI Sandbox">
            <BlankAISandbox />
          </RouteElement>
        }
      />
      <Route
        path={`${AppRoutePath.EXTERNAL}/read-case`}
        element={
          <RouteElement title="Opening from outside">
            <ReadCase />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.EXTERNAL_VIEW_CASE}
        element={
          <RouteElement title="External View Case">
            <ViewCase />
          </RouteElement>
        }
      />
      <Route
        path={AppRedirectPath.VIEWER}
        element={<BackwardsCompatibilityRedirect path={AppRoutePath.VIEWER} />}
      />
      <Route
        path={AppRoutePath.VIEWER_WARD}
        element={
          <RouteElement title="WARD">
            <Ward />
          </RouteElement>
        }
      />
      <Route
        path={AppRedirectPath.REPORTER}
        element={<BackwardsCompatibilityRedirect path={AppRoutePath.REPORTER} />}
      />
      <Route path={AppRoutePath.LOGIN} element={<Navigate to={AppRoutePath.WORKLIST} />} />
      <Route
        path={AppRoutePath.PIXEL_WORKER}
        element={
          <RouteElement title="Worker">
            <OpenWorker />
          </RouteElement>
        }
      />
      <Route path="/" element={<Navigate to={AppRoutePath.WORKLIST} />} />
      {/* This is needed to ensure old bookmarks keep working */}
      <Route path={AppRoutePath.HOME} element={<Navigate to={AppRoutePath.WORKLIST} />} />
      <Route
        path="*"
        element={
          <RouteElement>
            <FourOFour />
          </RouteElement>
        }
      />

      <Route
        path={AppRoutePath.SYSTEM_CHECK}
        element={
          <RouteElement title="System Check">
            <SystemCheck />
          </RouteElement>
        }
      />
      <Route
        path={AppRoutePath.DEBUG}
        element={
          <RouteElement title="Debug">
            <Debug />
          </RouteElement>
        }
      />
    </Route>
  );

  return <Routes>{routes}</Routes>;
}

export const ReporterElement = () => {
  const location = useLocation();
  const closePath = `${location.pathname}${location.search}`;

  return (
    <>
      <Outlet />
      <Suspense>
        <BrowserPath
          browser={<TemplatesBrowser closePath={closePath} />}
          hash="#report-templates-browser"
          location={location}
        />
        <BrowserPath
          browser={<MacrosBrowser closePath={closePath} />}
          hash="#report-macros-browser"
          location={location}
        />
      </Suspense>
    </>
  );
};

type BackwardsCompatibilityRedirectProps = {
  path: string;
};

const BackwardsCompatibilityRedirect = ({ path }: BackwardsCompatibilityRedirectProps) => {
  const params = useParams();
  // @ts-expect-error [EN-10263] - TS18048 - 'params.worklistId' is possibly 'undefined'.
  const studyIds = params.worklistId.split(',');
  const { worklistId, priorIds } = studyIds.reduce(
    ({ worklistId, priorIds }, studyId) => {
      if (studyId.startsWith('w_')) {
        // @ts-expect-error [EN-10263] - TS2322 - Type 'string' is not assignable to type 'undefined'.
        worklistId = studyId.slice(2);
      } else if (studyId.startsWith('s_')) {
        // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'string' is not assignable to parameter of type 'never'.
        priorIds.push(studyId.slice(2));
      }
      return { worklistId, priorIds };
    },
    { worklistId: undefined, priorIds: [] }
  );
  const priorsSuffix = getCaseStudyParamsSuffix(priorIds.join(','), '');
  const newPath = `${generatePath(path, { ...params, worklistId })}${priorsSuffix}`;
  return <Navigate replace to={newPath} />;
};

const BrowserPath = ({
  browser,
  hash,
  location,
}: {
  browser: React.ReactNode;
  hash: string;
  location?: Location;
}) => (location?.hash === hash ? browser : null);

const reporterRoutes = (
  <>
    <Route
      path={ReporterRoutePath.TEMPLATE_EDIT}
      element={
        <RouteElement title="Reporter Template | Edit">
          <TemplateEditor />
        </RouteElement>
      }
    />
    <Route
      path={ReporterRoutePath.TEMPLATE_CLONE}
      element={
        <RouteElement title="Reporter Template | Clone">
          <TemplateEditor />
        </RouteElement>
      }
    />
    <Route
      path={ReporterRoutePath.TEMPLATE_NEW}
      element={
        <RouteElement title="Reporter Template | New">
          <TemplateEditor />
        </RouteElement>
      }
    />
    <Route
      path={ReporterRoutePath.MACRO_EDIT}
      element={
        <RouteElement title="Reporter Macro | Edit">
          <MacroEditor />
        </RouteElement>
      }
    />
    <Route
      path={ReporterRoutePath.MACRO_CLONE}
      element={
        <RouteElement title="Reporter Macro | Clone">
          <MacroEditor />
        </RouteElement>
      }
    />
    <Route
      path={ReporterRoutePath.MACRO_NEW}
      element={
        <RouteElement title="Reporter Macro | New">
          <MacroEditor />
        </RouteElement>
      }
    />
  </>
);
